import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Image } from "lucide-react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { toast } from "react-toastify";
import { getCampaign, getAdSets } from "../../hook/useAdsApi";
import {
  AD_OBJECTIVES,
  campaignStatusBadgeClasses,
  getCampaignDisplayLabel,
  formatSubmittedAt,
} from "./adsConstants";

export default function ViewCampaign() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [adSets, setAdSets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getCampaign(campaignId), getAdSets(campaignId)])
      .then(([campaignResult, adSetsResult]) => {
        setCampaign(campaignResult);
        setAdSets(adSetsResult);
      })
      .catch(() => toast.error("Failed to load campaign", { toastId: "campaign-load-error" }))
      .finally(() => setLoading(false));
  }, [campaignId]);

  const objective = AD_OBJECTIVES.find((o) => o.value === campaign?.objective);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 p-6 pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-3xl">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                  ←
                </button>
                <h1 className="text-xl font-bold">View Campaign : ID: CMP-{campaignId}</h1>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/ads/campaigns/${campaignId}/ad-sets/new`)}
                className="flex items-center gap-2 bg-[#095228] text-white font-semibold px-4 py-2 rounded-full text-sm"
              >
                + Create Ad Set
              </button>
            </div>
            <div className="w-full h-1 bg-[#d9d9d9] rounded-full mb-6" />

            {loading || !campaign ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : (
              <>
                {/* Exact colors from Figma's View Campaign screen (read-only access, matched
                    from a shared screenshot) - same gradient as the dashboard's 7-Day Spend
                    card. Campaign has no "spent" column today - Budget Used is shown as $0
                    honestly rather than a fabricated fill. */}
                <div
                  className="rounded-[26px] p-5 mb-4 text-white relative"
                  style={{ backgroundImage: "linear-gradient(256deg, #1de78b 2.6%, #08aeea 97.5%)" }}
                >
                  <span className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <Image size={16} className="text-white" />
                  </span>
                  <span className="inline-block bg-white text-[#131b2e] text-xs font-semibold px-3 py-1 rounded-full mb-3">
                    Schedule : {campaign.start_date} - {campaign.end_date}
                  </span>
                  <p className="text-sm font-bold">Budget Used</p>
                  <p className="text-4xl font-semibold mt-1">$ 0.00</p>
                  <p className="text-sm font-bold text-[#161f28] mt-1">Out of : ${campaign.budget}</p>
                </div>

                <div className="rounded-2xl border p-4 mb-6 bg-[#EAF6EE]">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${campaignStatusBadgeClasses(campaign.status)}`}>
                      {getCampaignDisplayLabel(campaign.status)}
                    </span>
                    <span className="text-xs text-[#747474]">ID: CMP-{campaignId}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="w-9 h-9 rounded-lg bg-white border border-[#dae2fd] flex items-center justify-center shrink-0">
                      {objective?.iconSrc && <img src={objective.iconSrc} alt="" className="w-6 h-6" />}
                    </span>
                    <p className="font-bold">{campaign.name}</p>
                  </div>
                  <p className="text-xs text-[#9a9a9a] mt-3 pt-3 border-t">
                    Submitted {formatSubmittedAt(campaign.created_at)}
                  </p>
                </div>

                <h2 className="text-lg font-semibold mb-1">Campaign Objective</h2>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-9 h-9 rounded-lg bg-white border border-[#dae2fd] flex items-center justify-center shrink-0">
                    {objective?.iconSrc && <img src={objective.iconSrc} alt="" className="w-6 h-6" />}
                  </span>
                  <p className="text-sm text-[#434656]">{objective?.label || campaign.objective}</p>
                </div>

                <h2 className="text-lg font-semibold mb-2">Your Ad sets</h2>
                <div className="space-y-4">
                  {adSets.length === 0 && <p className="text-[#747474] text-sm">No ad sets yet.</p>}
                  {adSets.map((adSet) => (
                    <div
                      key={adSet.id}
                      onClick={() => navigate(`/ads/ad-sets/${adSet.id}`)}
                      className="rounded-2xl border p-4 cursor-pointer hover:shadow-md transition bg-[#EAF6EE]"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${campaignStatusBadgeClasses(adSet.status)}`}>
                          {getCampaignDisplayLabel(adSet.status)}
                        </span>
                        <span className="text-xs text-[#747474]">ID: ADSET-{adSet.id}</span>
                      </div>
                      <p className="font-semibold text-[#131b2e] mt-2">{adSet.name}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <span className="text-[#747474]">Target Type</span>
                        <span className="bg-[#095228] text-white px-3 py-1 rounded-full text-xs capitalize">
                          {adSet.target_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
